import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";

import Header from "../../../components/header";
import Navbar from "../../../components/navbar";

export default function TestAnswer() {
  const location = useLocation();
  const planData = location.state?.planData;

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const dates = dateStr.split("–");
    if (dates.length !== 2) return "";

    const formatSingleDate = (date: string) => {
      const d = new Date(date.trim());
      return `${d.getDate()}/${d.getMonth() + 1}`;
    };

    return `${formatSingleDate(dates[0])} - ${formatSingleDate(dates[1])}`;
  };

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <Header />
      <Navbar />

      <Helmet>
        <title>Your AI Travel Plan - Explore Asia</title>
        <meta
          name="description"
          content="View your personalized AI-generated travel itinerary with detailed day-by-day plans, local food recommendations, and travel tips."
        />
      </Helmet>

      <div className="w-full flex justify-center">
        <div
          className={`max-w-[888px] w-full h-full flex flex-col items-center ${
            windowWidth > 800 ? "px-0" : "px-0"
          } py-8`}
        >
          <div
            className={`w-full bg-white ${
              windowWidth > 800 ? "rounded-lg shadow-xl p-6" : "px-4"
            } `}
          >
            <div
              className={`flex ${
                windowWidth > 800 ? "w-[76%] items-center" : "w-full flex-col"
              }  p-6 bg-slate-0`}
            >
              <h1 className="text-4xl font-extrabold mr-4">
                Trip to {planData.destination}
              </h1>
              <div className="flex items-end pt-2">
                <img src="/icon/date2.png" alt="" className="w-5 h-5" />
                <span className="text-[#636c75] text-sm font-bold ml-1">
                  {formatDate(planData?.travelPeriod)}
                </span>
              </div>
            </div>
            <div
              className={`w-full ${
                windowWidth > 800 ? "px-16" : "px-4"
              } pt-6 bg-[#fff]`}
            >
              {/* 行程安排部分 */}
              <div className="mb-12">
                <div className="flex items-center mb-6">
                  <img src="/icon/list.png" alt="" className="w-6 h-5 mr-2" />
                  <h2 className="text-3xl font-extrabold">Itinerary</h2>
                </div>
                <div className="space-y-6">
                  {planData?.itinerary?.map(
                    (
                      day: {
                        day: string;
                        date: string;
                        schedule: Array<{ time: string; activity: string }>;
                      },
                      index: number
                    ) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-xl font-bold mb-4">
                          {day.day} - {day.date}
                        </h3>
                        <div className="space-y-4">
                          {day.schedule.map((item, idx) => (
                            <div key={idx} className="flex">
                              <span className="text-[#5b68d3] font-semibold w-20">
                                {item.time}
                              </span>
                              <span className="flex-1">{item.activity}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* 景點推薦部分 */}
              <div className="mb-12">
                <div className="flex items-center mb-6">
                  <img src="/icon/label.png" alt="" className="w-5 h-5 mr-2" />
                  <h2 className="text-3xl font-extrabold">Top Attractions</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {planData?.topAttractions?.map(
                    (
                      attraction: {
                        name: string;
                        type: string;
                        description: string;
                        recommendedTime: string;
                      },
                      index: number
                    ) => (
                      <div key={index} className="bg-gray-50 rounded-lg p-6">
                        <h3 className="text-lg font-bold mb-2">
                          {attraction.name}
                        </h3>
                        <div className="text-sm text-gray-600 mb-2">
                          {attraction.type}
                        </div>
                        <p className="text-gray-800 mb-4">
                          {attraction.description}
                        </p>
                        <div className="text-sm font-medium text-[#5b68d3]">
                          Recommended time: {attraction.recommendedTime}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* 季節亮點部分 */}
              <div className="mb-12">
                <div className="flex items-center mb-6">
                  <img
                    src="/icon/highlight.png"
                    alt=""
                    className="w-5 h-5 mr-2"
                  />
                  <h2 className="text-3xl font-extrabold">
                    Seasonal Highlights
                  </h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-1 gap-6">
                  {planData?.seasonalHighlights?.length > 0 ? (
                    planData.seasonalHighlights.map(
                      (
                        highlight: {
                          name: string;
                          type: string;
                          description: string;
                          eventDate: string;
                        },
                        index: number
                      ) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-6">
                          <div className="flex items-center">
                            <h3 className="text-lg font-bold mb-2">
                              {highlight.name}
                            </h3>
                            <span className="text-sm text-gray-600 mb-2 ml-3">
                              ({highlight.type})
                            </span>
                          </div>

                          <p className="text-gray-800 mb-2">
                            {highlight.description}
                          </p>
                          <div className="text-sm font-semibold text-[#5b68d3]">
                            When: {highlight.eventDate}
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <div>....</div>
                  )}
                </div>
              </div>
              {/* 當地活動部分 */}
              <div className="mb-12">
                <div className="flex items-center mb-6">
                  <img src="/icon/event.png" alt="" className="w-5 h-5 mr-2" />
                  <h2 className="text-3xl font-extrabold">Local Events</h2>
                </div>
                <div className="grid grid-cols-1 gap-6">
                  {planData?.localEvents?.length > 0 ? (
                    planData.localEvents.map(
                      (
                        event: {
                          name: string;
                          type: string;
                          description: string;
                          eventDate: string;
                        },
                        index: number
                      ) => (
                        <div key={index} className="bg-gray-50 rounded-lg p-6">
                          <div className="flex items-center">
                            <h3 className="text-lg font-bold mb-2">
                              {event.name}
                            </h3>
                            <span className="text-sm text-gray-600 mb-2 ml-3">
                              ({event.type})
                            </span>
                          </div>

                          <p className="text-gray-800 mb-2">
                            {event.description}
                          </p>
                          <div className="text-sm font-semibold text-[#5b68d3]">
                            When: {event.eventDate}
                          </div>
                        </div>
                      )
                    )
                  ) : (
                    <div>....</div>
                  )}
                </div>
              </div>

              {/* 旅行貼士部分 */}
              <div className="mb-12">
                <div className="flex items-center mb-6">
                  <img src="/icon/doc1.png" alt="" className="w-4 h-4 mr-2" />
                  <h2 className="text-3xl font-extrabold">Travel Tips</h2>
                </div>
                <div className="bg-gray-50 rounded-lg p-6">
                  <ul className="list-disc list-inside space-y-3">
                    {planData?.travelTips?.map((tip: string, index: number) => (
                      <li
                        key={index}
                        className="text-gray-800 font-medium flex items-center"
                      >
                        <b className="mr-2 text-[8px]">●</b>
                        {tip}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              {/* 返回按鈕 */}
              <div className="flex justify-center pb-8">
                <button
                  type="button"
                  onClick={() => window.history.back()}
                  className="px-16 py-3 text-center text-white font-extrabold rounded-full bg-[#5b68d3] hover:bg-[#4752c6] disabled:bg-gray-400"
                >
                  Back to AI Planner
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
